/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type ReactNode, useCallback, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import { fg } from '@atlaskit/platform-feature-flags/fg';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Grid, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import type { SearchPageNode } from '@atlassian/search-client/search-page/types';
import { serveFpsMultiSiteSearchExperiment } from '@atlassian/search-common/experiments';

import { type ApplicationModes } from '../../constants/application-modes';
import { type PrimaryProductKey } from '../../constants/product-keys';
import type { ProductConfigs } from '../../constants/products';
import type { ResultClickedCallback } from '../../constants/types';
import { extractCloudIdFromAri } from '../../utils/sites';
import { useCrossAppLinkHandlers } from '../../utils/use-cross-app-link-handlers';
import { useSearchCrossProductUrlWrapper } from '../../utils/use-cross-product-url-wrapper';
import { useAppContext } from '../app-context';
import { SearchMetadata } from '../search-metadata';
import { SearchResultTitle } from '../search-result-title';
import { messages } from './messages';

const searchResultContainerStyles = xcss({
	position: 'relative',
});

const iconAreaStyles = xcss({
	gridArea: 'icon',
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'center',
	width: token('space.600'),
});

const newIconStyles = xcss({
	marginBlockStart: 'space.075',
});

const bodyAreaStyles = xcss({
	gridArea: 'body',
	paddingTop: 'space.050',
	overflowWrap: 'break-word',
});

const bodyAreaHiddenStyles = xcss({
	display: 'none',
});

const footerAreaStyles = xcss({
	gridArea: 'footer',
});

const lockIconStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	marginInlineEnd: 'space.075',
	paddingTop: 'space.025',
});

const headerRightContainerStyles = xcss({
	gridArea: 'header-right',
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'flex-end',
});

export type SearchResultProps = {
	id?: string;
	product?: string;
	/** Product used for the visible metadata label when it differs from the interaction product. */
	metadataProduct?: string;
	productSelected?: boolean;
	primaryProduct?: PrimaryProductKey;
	primarySubProduct?: string;
	description?: ReactNode;
	footer?: ReactNode;
	icon: ReactNode;
	headerMetadataParts?: any[]; // TODO: CHANGE THIS TO A MORE SPECIFIC TYPE
	headerMetadataSocialParts?: any[]; // TODO: CHANGE THIS TO A MORE SPECIFIC TYPE
	titleMetadata?: ReactNode;
	/** Passed to `SearchResultTitle`; see its `titleActions`. */
	titleActions?: ReactNode;
	title: ReactNode;
	url?: string;
	isThirdParty?: boolean;
	hideSummarizeInChat?: boolean;
	position?: number;
	/** The ARIA role for the search result */
	role?: string;
	/**
	 * @private
	 * @deprecated Passing in the full partial is ignored by the component. Pass in the required props directly instead.
	 * TODO: https://product-fabric.atlassian.net/browse/QS-7698
	 **/
	partial?: SearchPageNode;
	cloudId?: string;
	appMode?: ApplicationModes;
	/** Called when the result is clicked */
	onResultClicked?: ResultClickedCallback;

	/** Called when the result context menu is opened */
	onResultContextMenu?: VoidFunction;

	/** Called when the copy link button is clicked */
	onCopyLinkButtonClicked?: VoidFunction;

	/** Called when the summarize in chat button is clicked */
	onSummarizeInChatButtonClicked?: VoidFunction;

	/** Called when the context menu copy button is clicked */
	onContextMenuCopyButtonClicked?: VoidFunction;

	/** Called when the context menu button is clicked */
	onContextMenuButtonClicked?: VoidFunction;
	isLoadingSkeleton?: boolean;
	linkedResults?: SearchPageNode;
	isLinkedResult?: boolean;
	/** Used to render the lock icon in result context menu */
	isPrivate?: boolean;
	/** Used to determine if the result has any related links */
	hasRelatedLinks?: boolean;
	mainContentWidth?: number;
	/** Forwarded to SearchMetadata to derive the owning-site name at the display point. */
	selectedSites?: ReadonlyMap<string, string>;
	productConfigs?: ProductConfigs;
};

/**
 * This component is intended to be as generic as possible.
 * It should provide generic "slots" for users of this component to add
 * their content in.
 */
export const SearchResult: ({
	id,
	product,
	metadataProduct,
	productSelected,
	primaryProduct,
	primarySubProduct,
	description,
	footer,
	icon,
	url,
	hideSummarizeInChat,
	role,
	cloudId,
	title,
	isThirdParty,
	appMode,
	headerMetadataParts,
	headerMetadataSocialParts,
	titleMetadata,
	titleActions,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	isLoadingSkeleton,
	isLinkedResult,
	isPrivate,
	hasRelatedLinks,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchResultProps) => JSX.Element = ({
	id,
	product,
	metadataProduct,
	productSelected,
	primaryProduct,
	primarySubProduct,
	description,
	footer,
	icon,
	url,
	hideSummarizeInChat = false,
	role = 'listitem',
	cloudId, // required for AI Summaries
	title,
	isThirdParty,
	appMode,
	headerMetadataParts,
	headerMetadataSocialParts,
	titleMetadata,
	titleActions,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	isLoadingSkeleton = false,
	isLinkedResult = false,
	isPrivate,
	hasRelatedLinks = false,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchResultProps) => {
	const { isRovoLLMEnabled } = useAppContext();
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);
	const containerRef = useRef<HTMLDivElement | null>(null);

	const isRovoLLMCheckPassed = !fg('kd_summarize_in_chat_rovo_llm_check') || isRovoLLMEnabled;
	const isNonPrimarySiteResult =
		serveFpsMultiSiteSearchExperiment({
			primaryProduct,
			fireExperimentExposure: false,
		}) !== 'control' && cloudId !== extractCloudIdFromAri(id);

	const showSummarizeInChatByTitle =
		isRovoLLMCheckPassed && !hideSummarizeInChat && !isNonPrimarySiteResult;

	const [hasDescriptionContent, setHasDescriptionContent] = useState(
		isLoadingSkeleton ? true : !!description,
	);
	const descriptionRef = useCallback(
		(node: HTMLDivElement | null) => {
			// The loading skeleton intentionally renders a description made of empty
			// styled boxes (no text / media nodes). Skip the "is this empty?" heuristic
			// when rendering as a skeleton so the body row is preserved and the skeleton
			// keeps its full vertical height.
			if (isLoadingSkeleton) {
				return;
			}
			if (node) {
				const hasContent =
					(node.textContent?.trim().length ?? 0) > 0 ||
					node.querySelector('img, svg, canvas, video') !== null;
				if (!hasContent) {
					setHasDescriptionContent(false);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only re-check when description identity or skeleton mode changes
		[description, isLoadingSkeleton],
	);

	const wrapCrossProductUrl = useSearchCrossProductUrlWrapper({
		bridge: 'searchPage',
		primaryProduct,
		primarySubProduct,
	});
	const wrappedUrl = wrapCrossProductUrl(url, product);
	// Reports the cross-app interaction using the `xpis` param already on
	// `wrappedUrl`. A no-op when the URL was not wrapped.
	const crossAppLinkHandlers = useCrossAppLinkHandlers({
		href: wrappedUrl,
		destinationApp: product,
	});

	return (
		<Box
			role={role}
			onMouseEnter={() => {
				setIsHovered(true);
			}}
			onMouseLeave={() => setIsHovered(false)}
			testId={isLoadingSkeleton ? 'search-page-result-skeleton' : 'search-page-result'}
			ref={containerRef}
			xcss={searchResultContainerStyles}
		>
			<Grid
				columnGap="space.150"
				rowGap="space.050"
				templateAreas={[
					'icon heading header-right',
					'icon subheading-left header-right',
					...(hasDescriptionContent || isLoadingSkeleton ? ['icon body body'] : []),
					...(footer ? ['icon footer footer'] : []),
				]}
				templateColumns="auto 1fr auto"
			>
				<Box xcss={iconAreaStyles}>
					<Box xcss={newIconStyles}>{icon}</Box>
				</Box>
				<SearchResultTitle
					id={id}
					title={title}
					url={wrappedUrl}
					crossAppLinkHandlers={crossAppLinkHandlers}
					isThirdParty={isThirdParty}
					appMode={appMode}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultContextMenu}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					isHovered={isHovered}
					titleMetadata={titleMetadata}
					titleActions={titleActions}
					showSummarizeInChatButton={showSummarizeInChatByTitle}
					hasRelatedLinks={hasRelatedLinks}
				/>
				<SearchMetadata
					productConfigs={productConfigs}
					searchResultId={id}
					product={metadataProduct ?? product}
					productSelected={productSelected}
					parts={headerMetadataParts}
					socialParts={headerMetadataSocialParts}
					containerRef={containerRef}
					// The skeleton's single placeholder part must never be measured as overflowing,
					// otherwise the "More" popup button (the three-dots "show more horizontal" icon)
					// renders in the loading state.
					disableOverflowObserver={
						isLoadingSkeleton && fg('rovo_fps_skip_skeleton_metadata_overflow')
					}
					selectedSites={selectedSites}
				/>
				<Box xcss={headerRightContainerStyles}>
					{isPrivate && (
						<Box xcss={lockIconStyles}>
							<Tooltip content={formatMessage(messages.messageResultLockIconTooltip)}>
								<LockLockedIcon
									label={formatMessage(messages.privateChannelLabel)}
									color={token('color.icon.accent.red')}
									size="small"
								/>
							</Tooltip>
						</Box>
					)}
				</Box>
				{description && (
					<Box
						ref={descriptionRef}
						xcss={[
							bodyAreaStyles,
							!hasDescriptionContent && !isLoadingSkeleton && bodyAreaHiddenStyles,
						]}
					>
						{description}
					</Box>
				)}
				{footer && <Box xcss={footerAreaStyles}>{footer}</Box>}
			</Grid>
		</Box>
	);
};
